<script>
import { mapState } from 'vuex';
import isFunction from 'lodash/isFunction';
import flatPickr from 'vue-flatpickr-component';
import ReportModel from '@models/Report';
import ExpenseModel from '@models/Expense';
import CostTypeModel from '@models/CostType';
import BlockingContainer from '@components/BlockingContainer';
import editFormChange from '@mixins/editFormChange';
import datePickerUtility from '@mixins/datePickerUtility';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import _find from 'lodash/find';
import FileWrapper from '@components/FileWrapper';
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';
import FileDownloadLink from '@components/FileDownloadLink';

export default {
  components: {
    BlockingContainer,
    flatPickr,
    FontAwesomeIcon,
    FileWrapper,
    VueCropper,
    FileDownloadLink,
  },
  mixins: [editFormChange, datePickerUtility],
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    // eslint-disable-next-line
    report: {
      required: true,
    },
  },
  data() {
    return {
      expense: {
        report_id: this.report.id,
        cost_type_id: null,
        cost_type: null,
        description: null,
        date: null,
        amount: null,
        paid: 0,
        export: 1,
        client: null,
        scan: [],
        scan_file: null,
        scan_meta_data: {
          height: 0,
          scaleX: 1,
          scaleY: 1,
          width: 0,
          x: 0,
          y: 0,
        },
      },
      costTypes: [],
      costTypeModel: CostTypeModel,
      defaultExpense: {},
      statuses: [
        { value: 0, text: 'Not paid' },
        { value: 1, text: 'Paid' },
      ],
    };
  },
  computed: {
    ...mapState({
      dateConfig: (state) => state.common.dateConfig,
    }),
  },
  watch: {
    'expense.scan': function (val) {
      this.expense.scan_file = null;
      this.$nextTick(() => {
        const entity = Array.isArray(val) ? val[0] : val;
        if(entity instanceof File && entity.type !== 'application/pdf') {
          if (typeof FileReader === 'function') {
            const reader = new FileReader();
            reader.onload = (event) => {
              this.expense.scan_file = event.target.result;
            };
            reader.readAsDataURL(entity);
          }
        }
      });
    },
    'expense.cost_type_id': function (val) {
      if (val != null) {
        // find cost type by id
        const costType = _find(this.costTypes, function (ct) {
          return ct.id === val;
        });

        // set client prefs by selected cost type
        if (costType) {
          this.expense.client = costType.client;
        }
      }
    },
  },
  mounted() {
    this.defaultExpense = Object.assign({}, this.expense);
    this.$wait.start('cost types loading');
    CostTypeModel.select(['id', 'name', 'client'])
      .orderBy(`name`)
      .page(1)
      .limit(100)
      .get()
      .then((response) => {
        this.costTypes = response.data;
        this.$wait.end('cost types loading');
      })
      .catch(() => {
        this.$wait.end('cost types loading');
      });
    if (this.loadedId) {
      this.$wait.start('expense loading');
      new ReportModel({ id: this.report.id })
        .expenses()
        .find(this.loadedId)
        .then((response) => {
          this.expense = Object.assign({}, this.expense, response.expense);
          this.$wait.end('expense loading');
          this.$nextTick(() => {
            this.initChangeWatcher(['expense'], this.validate);
          });
        });
    }
  },
  methods: {
    validate(callback) {
      this.$validator.validateAll('expense-form').then((result) => {
        if (result) {
          this.$wait.start('expense loading');
          this.submit(callback);
        }
      });
    },
    submit(callback) {
      const report = new ReportModel({ id: this.report.id });

      // clear scan file - don't send unnecessary data
      delete this.expense.scan_file;

      new ExpenseModel(
        Object.assign({}, this.expense, {
          scan: this.expense.scan && Array.isArray(this.expense.scan) ? this.expense.scan[0] : this.expense.scan,
        })
      )
        .for(report)
        .save()
        .then((response) => {
          this.expense = Object.assign({}, this.defaultExpense);
          this.$validator.reset();
          this.$emit('submitted', response.expense);
          if (callback && isFunction(callback)) callback();
          this.$wait.end('expense loading');
        })
        .catch(() => this.$wait.end('expense loading'));
    },
    getCropData() {
      this.expense.scan_meta_data = this.$refs.cropper.getData(true)
    },
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('expense loading') || $wait.is('cost types loading')"></BlockingContainer>
    <b-form data-vv-scope="expense-form" @submit.prevent="validate">
      <b-form-row>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.cost_type') }}:
              <span class="status-star text-danger">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="costTypePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="costTypePopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.cost_type') }}
                </b-popover>
              </span>
            </template>
            <b-form-select
              v-model="expense.cost_type_id"
              v-validate="'required'"
              data-vv-value-path="localValue"
              data-vv-name="cost_type_id"
              :data-vv-as="$t('fields.cost_type')"
              :state="errors.has('cost_type_id', 'expense-form') ? false : null"
              aria-describedby="input-feedback-cost_type_id"
            >
              <option :value="null">{{ $t('placeholders.cost_type') }}</option>
              <option v-for="costType in costTypes" :key="`costTypeSelect${costType.id}`" :value="costType.id">
                {{ costType.name }}
              </option>
            </b-form-select>

            <b-form-invalid-feedback id="input-feedback-cost_type_id">
              <template v-show="errors.has('cost_type_id', 'expense-form')">{{ errors.first('cost_type_id', 'expense-form') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.date') }}:
              <span class="status-star text-success">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="expenseDatePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="expenseDatePopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.expense_date') }}
                </b-popover>
              </span>
            </template>
            <div :class="{ 'is-invalid': errors.has('date', 'expense-form') }">
              <flat-pickr
                v-model="expense.date"
                v-validate="''"
                data-vv-name="date"
                :data-vv-as="$t('fields.date')"
                aria-describedby="input-feedback-date"
                :config="dateConfig"
                class="form-control"
                :class="{ 'is-invalid': errors.has('date', 'expense-form') }"
                :placeholder="$t('placeholders.select_date')"
                @on-close="closeCalendar('expense.date', 'dd.MM.yyyy', 'yyyy-MM-dd', ...arguments)"
              >
              </flat-pickr>
              <b-form-invalid-feedback id="input-feedback-date">
                <template v-show="errors.has('date', 'expense-form')">{{ errors.first('date', 'expense-form') }} </template>
              </b-form-invalid-feedback>
            </div>
          </b-form-group>
        </b-col>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.amount') }}:
              <span class="status-star text-success">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="amountPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="amountPopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.amount') }}
                </b-popover>
              </span>
            </template>
            <b-form-input
              v-model="expense.amount"
              v-validate="{ regex: /^\d{1,}(?:\,\d{1,3})?$/ }"
              data-vv-value-path="localValue"
              data-vv-name="amount"
              :data-vv-as="$t('fields.amount')"
              type="text"
              :state="errors.has('amount', 'expense-form') ? false : null"
              aria-describedby="input-feedback-amount"
              :placeholder="$t('placeholders.enter_amount')"
            >
            </b-form-input>

            <b-form-invalid-feedback id="input-feedback-amount">
              <template v-show="errors.has('amount', 'expense-form')">{{ errors.first('amount', 'expense-form') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col sm="3">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.export') }}
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="exportPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="exportPopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.export_expenses') }}
                </b-popover>
              </span>
            </template>
            <b-form-checkbox v-model="expense.export" :value="1" :unchecked-value="0" switch>&nbsp;</b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col sm="12">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.scan') }}:
              <span class="status-star text-success">*</span>
            </template>
            <div v-if="expense.scan && !Array.isArray(expense.scan)">
            <template  v-if="expense.scan.type !== 'application/pdf'">
              <img
                :src="expense.scan.small"
                class="img-thumbnail mx-auto d-block"
                :class="$style.scanImg"
              />
              <b-btn
                variant="outline-danger"
                :disabled="report.completed === 1 && ($can.not('doAccounting') || $can.not('superAdministrate'))"
                @click.stop="expense.scan = []">{{ $t('actions.remove') }}</b-btn>
            </template>
            <FileDownloadLink
              v-else
              :file="expense.scan"
              @remove="expense.scan = []"
            />
            </div>
            <FileWrapper
              v-else
              v-model="expense.scan"
              v-validate="'mimes:image/jpeg,image/png,image/gif,image/jpg,application/pdf'"
              :data-vv-as="$t('fields.scan')"
              :state="errors.has('scan', 'expense-form') ? false : null"
              :placeholder="$t('placeholders.scan')"
              :disabled="report.completed === 1 && ($can.not('doAccounting') || $can.not('superAdministrate'))"
              accept="image/*,application/pdf"
              aria-describedby="input-feedback-scan"
              name="scan"
            >
            </FileWrapper>
            <VueCropper
              v-if="expense.scan_file !== null"
              ref="cropper"
              :src="expense.scan_file"
              drag-mode="crop"
              :auto-crop-area="0.5"
              :cropend="getCropData"
              :zoom="getCropData"
              :img-style="{ width: 'auto', height: '600px' }"
            >
            </VueCropper>

            <div v-show="errors.has('scan', 'expense-form')" class="invalid-feedback d-block mt-1">
              {{ errors.first('scan', 'expense-form') }}
            </div>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.description_optional') }}:
          <span class="status-star text-success">*</span>
          <span class="near-label-info text-secondary" @click.stop>
            <FontAwesomeIcon id="amountDescriptionPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
            <b-popover target="amountDescriptionPopover" triggers="focus" placement="rightbottom">
              {{ $t('infos.amount_description') }}
            </b-popover>
          </span>
        </template>
        <b-form-input
          id="expenseTextarea"
          v-model="expense.description"
          v-validate="'max:65000'"
          data-vv-value-path="localValue"
          data-vv-name="description"
          :data-vv-as="$t('fields.description')"
          :placeholder="$t('placeholders.description')"
          :state="errors.has('description', 'expense-form') ? false : null"
          aria-describedby="input-feedback-description"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-description">
          <template v-show="errors.has('description', 'expense-form')">
            {{ errors.first('description', 'expense-form') }}
          </template>
        </b-form-invalid-feedback>
      </b-form-group>

      <b-button
        ref="expenseSubmitBtn"
        type="submit"
        :disabled="errors.any('expense-form')"
        :variant="'outline-' + (!errors.any('expense-form') ? 'dark' : 'danger')"
      >
        {{ $t('actions.submit') }}
      </b-button>
    </b-form>
  </div>
</template>
<style lang="scss" module>
@import '@design';

.scanImg {
  height: 160px;

  &:hover {
    cursor: pointer;
    border: 1px solid map_get($theme-colors, 'dark');
  }
}

.clearButtonContainer {
  position: absolute;
  top: 5px;
  right: 5px;
  padding: 0 !important;
}
</style>
